import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Player, TeamStatsDoc, LeagueStat } from '@/types';
import { TrendingUp, TrendingDown, ShieldCheck } from 'lucide-react';
import { leagueLine, avgLabelFor } from '@/config/season';

type SortKey = 'average' | 'highGame' | 'improvement' | 'name';

export const Roster: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [team, setTeam] = useState<TeamStatsDoc | null>(null);
  // Career bests need the per-season leagueStats rows (same sources as PlayerProfile)
  const [careerBests, setCareerBests] = useState<Record<string, { highGame: number; highSeries: number }>>({});
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('average');

  useEffect(() => {
    // Public reads only return published + consented players (firestore.rules)
    Promise.all([
      getDocs(query(collection(db, 'players'), where('published', '==', true), where('consentOnFile', '==', true))),
      getDoc(doc(db, 'teamStats', 'current')).catch(() => null),
    ])
      .then(([snap, teamSnap]) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }) as Player)
          // willardTeam guard: the program tracks other schools' bowlers too
          .filter((p) => p.isActive !== false && p.willardTeam !== false && p.removed !== true);
        setPlayers(list);
        if (teamSnap?.exists()) setTeam(teamSnap.data() as TeamStatsDoc);

        // Career high game/series across every source (mirrors PlayerProfile):
        // Stats-app snapshot + past-season leagueStats rows + live currentLeagues.
        Promise.all(
          list.map((p) =>
            getDocs(query(collection(db, 'leagueStats'), where('playerId', '==', p.id)))
              .then((r) => r.docs.map((d) => d.data() as LeagueStat))
              .catch(() => [] as LeagueStat[])
              .then((rows) => {
                const highGame = Math.max(
                  p.statsSnapshot?.highGame ?? 0,
                  ...rows.map((l) => l.highGame || 0),
                  ...(p.currentLeagues || []).map((l) => l.highGame || 0)
                );
                const highSeries = Math.max(
                  p.statsSnapshot?.highSeries ?? 0,
                  ...rows.map((l) => l.highSeries || 0),
                  ...(p.currentLeagues || []).map((l) => l.highSeries || 0)
                );
                return [p.id, { highGame, highSeries }] as const;
              })
          )
        ).then((entries) => setCareerBests(Object.fromEntries(entries)));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const metrics = (p: Player) => {
    // Current-season league line first (Fall 2026 / 26-27), else the summer line while
    // LeagueSecretary still lists it. Season rules live in src/config/season.json.
    const summer = leagueLine(p);
    // Book = USBC composite (official last-season average) — NOT the Stats-app blend.
    const book = p.lastSeasonBookAverage || 0;
    const bests = careerBests[p.id];
    return {
      // Current league average up front; USBC book if not currently bowling; never the Stats-app blend.
      average: summer?.currentAverage || book || 0,
      summerAvg: summer?.currentAverage || 0,
      avgLabel: avgLabelFor(summer),
      bookAvg: book,
      // Career bests fall back to what's on the doc while leagueStats loads
      highGame: bests?.highGame || summer?.highGame || 0,
      highSeries: bests?.highSeries || summer?.highSeries || 0,
      improvement: summer && book > 0 ? summer.currentAverage - book : 0,
      games: summer?.games || 0,
      over200: p.leagueGamesOver200 || 0,
      summer,
    };
  };

  const isJuniorGold = (p: Player) =>
    (p.usbcMemberships || []).some((m) => /junior gold/i.test(m.type)) ||
    (p.juniorGoldResults || []).length > 0;

  const sorted = [...players].sort((a, b) => {
    if (sortKey === 'name') return a.name.localeCompare(b.name);
    return metrics(b)[sortKey] - metrics(a)[sortKey];
  });

  return (
    <div className="bg-willard-black text-white min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-14">
        <p className="font-heading text-willard-taupe tracking-[0.4em] uppercase text-lg mb-2">Willard Tigers</p>
        <h1 className="font-display text-6xl mb-8">THE TEAM</h1>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <span className="text-sm text-willard-grey-500 mr-2">Sort by</span>
          {(
            [
              ['average', 'Average'],
              ['highGame', 'High Game'],
              ['improvement', 'Most Improved'],
              ['name', 'Name'],
            ] as [SortKey, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setSortKey(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${
                sortKey === key
                  ? 'bg-willard-taupe text-willard-black border-willard-taupe'
                  : 'border-willard-grey-700 text-willard-grey-300 hover:border-willard-taupe'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="min-h-[30vh] flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-willard-taupe" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="min-h-[30vh] flex flex-col items-center justify-center text-center gap-2">
            <p className="font-display text-2xl">Profiles coming soon</p>
            <p className="text-willard-grey-400 max-w-md">
              Athlete profiles go live as parental consent forms are turned in.
              {team ? ` ${team.activePlayers} Tigers are already tracking every game.` : ''}
            </p>
          </div>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {sorted.map((p) => {
                const m = metrics(p);
                return (
                  <Link
                    key={p.id}
                    to={`/player/${p.id}`}
                    className="bg-willard-grey-900 rounded-xl border border-willard-grey-800 hover:border-willard-taupe transition-colors overflow-hidden group"
                  >
                    <div className="flex items-center gap-4 p-5">
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-willard-grey-800 ring-2 ring-willard-grey-700 shrink-0">
                        {p.photoURL ? (
                          <img src={p.photoURL} alt={p.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center font-display text-2xl text-willard-grey-500">
                            {p.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-lg truncate group-hover:text-willard-taupe transition-colors">{p.name}</p>
                        <p className="text-xs text-willard-grey-500 flex items-center gap-2 flex-wrap">
                          {p.graduationYear ? `Class of ${p.graduationYear}` : p.grade ? `Grade ${p.grade}` : 'Tiger'}
                          {p.usbcId && (
                            <span className="inline-flex items-center gap-0.5 text-willard-taupe">
                              <ShieldCheck className="w-3 h-3" /> USBC
                            </span>
                          )}
                          {isJuniorGold(p) && (
                            <span className="px-1.5 py-0.5 rounded bg-yellow-500 text-willard-black font-bold text-[10px] uppercase tracking-wider">
                              Junior Gold
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-willard-grey-800 border-t border-willard-grey-800 bg-willard-black/40">
                      <div className="text-center py-3">
                        <p className="font-display text-2xl">{m.summerAvg || '—'}</p>
                        <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">{m.avgLabel}</p>
                      </div>
                      <div className="text-center py-3">
                        <p className="font-display text-2xl">{m.bookAvg || '—'}</p>
                        <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">Book Avg</p>
                      </div>
                      <div className="text-center py-3">
                        {m.improvement > 0 ? (
                          <p className="font-display text-2xl text-green-400 inline-flex items-center gap-1">
                            <TrendingUp className="w-4 h-4" />+{Math.round(m.improvement)}
                          </p>
                        ) : m.improvement < 0 ? (
                          <p className="font-display text-2xl text-willard-grey-400 inline-flex items-center gap-1">
                            <TrendingDown className="w-4 h-4" />{Math.round(m.improvement)}
                          </p>
                        ) : (
                          <p className="font-display text-2xl text-willard-grey-500">—</p>
                        )}
                        <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">Trend</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 divide-x divide-willard-grey-800 border-t border-willard-grey-800 bg-willard-black/40">
                      <div className="text-center py-3">
                        <p className="font-display text-2xl">{m.highGame || '—'}</p>
                        <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">Career High</p>
                      </div>
                      <div className="text-center py-3">
                        <p className="font-display text-2xl">{m.highSeries || '—'}</p>
                        <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">Career Series</p>
                      </div>
                      <div className="text-center py-3">
                        {m.over200 > 0 ? (
                          <>
                            <p className="font-display text-2xl text-willard-taupe">{m.over200}</p>
                            <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">200+ Games</p>
                          </>
                        ) : (
                          <>
                            <p className="font-display text-2xl">{m.games || '—'}</p>
                            <p className="text-[10px] uppercase tracking-widest text-willard-grey-500">League Games</p>
                          </>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
            {team && team.activePlayers > sorted.length && (
              <p className="text-sm text-willard-grey-500 mt-8">
                Plus {team.activePlayers - sorted.length} more Tigers tracking every frame. Profiles publish as
                consent forms come in.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};
